import { Box } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { colors, customEvent, LARGEL_BUTTON_TYPE, LargeButton, Spacing, VStack } from "goi_common";
import { useEffect, useState } from "react";

import CompareRequestModal from "./CompareRequestModal";
import { buildCompareBadges } from "@/containers/Parlor/ParlorRequestContainer/utils/buildCompareCandidates";

/**
 * 비용비교 인라인 블록 (상세 NotSafe 전용, 참여 확대용).
 *
 * 주변 장례식장 섹션 "위"에 배치. 주변 섹션 = 세로 일자형 "둘러보기",
 * 이 블록 = 이미지 배경 카드가 자동으로 흐르는 가로 마퀴로 인근 장례식장을 훑어주고
 * "한 번에 비교해줄까요?" 로 유도. 개별 비용을 비싸다/싸다로 단정하지 않고 사실 배지(#유형 #거리 #시설)만.
 * CTA 클릭 → 번호 수집 모달 → 홈질문요청(현장 캡처, 페이지 이탈 없음).
 * 노출 cmp_card-view, 모달 오픈 cmp_card-open, 제출 cmp_card-submit(모달 내).
 */
type CompareItem = {
  id: string | number;
  name: string;
  distance: string;
  image?: string;
  managedType?: string;
  hasParking?: boolean;
  hasMeal?: boolean;
  binsoCount?: number;
  hasTransit?: boolean;
};

const scroll = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

// 배지 규칙은 니즈별 뒷퍼널 비교 스텝과 공유 (단일 소스). 비용은 배지에 넣지 않는다.
const buildBadges = (p: CompareItem): string[] => buildCompareBadges(p);

function CompareCard({ p }: { p: CompareItem }) {
  const badges = buildBadges(p);
  return (
    <VStack
      css={{
        flexShrink: 0,
        width: "176px",
        borderRadius: "10px",
        overflow: "hidden",
        border: `1px solid ${colors.gray100}`,
        background: colors.white,
      }}
    >
      {/* 상단: 이미지 구간 */}
      <Box
        css={{
          width: "100%",
          height: "100px",
          background: colors.gray200,
          backgroundImage: p.image ? `url(${p.image})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {/* 하단: 텍스트 구간 (흰 배경, 어두운 글자로 또렷하게) */}
      <VStack css={{ width: "100%", padding: "10px", alignItems: "flex-start", gap: "6px" }}>
        <p
          className="body_14_m"
          css={{
            color: colors.gray900,
            width: "100%",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {p.name}
        </p>
        <Box css={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
          {badges.map((b) => (
            <span
              key={b}
              className="caption_12_r"
              css={{
                color: colors.gray600,
                background: colors.gray50,
                border: `1px solid ${colors.gray100}`,
                borderRadius: "9999px",
                padding: "2px 7px",
              }}
            >
              #{b}
            </span>
          ))}
        </Box>
      </VStack>
    </VStack>
  );
}

export default function CompareLeadBlock({
  items,
  parlorName,
  regionLabel,
}: {
  items?: CompareItem[];
  parlorName: string;
  regionLabel: string;
}) {
  const list = (items ?? []).filter((i) => i.name);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (list.length > 0) {
      customEvent({ newGtm: { path: "parlor_detail", trigger_and_target: "cmp_card-view" } });
    }
  }, [list.length]);

  if (list.length === 0) return null;

  const onClickCompare = () => {
    customEvent({ newGtm: { path: "parlor_detail", trigger_and_target: "cmp_card-open" } });
    setOpen(true);
  };

  const region = regionLabel.split(/\s+/)[1] || "이 지역";
  const names = list.map((i) => i.name);
  const desc =
    names.length >= 3 ? (
      <>
        {names[0]}, {names[1]} 외 {names.length - 2}곳이 있어요.
        <br />
        <b>비용, 시설 정보, 접근성까지 한 번에</b> 알아보기
      </>
    ) : (
      <>
        {names.join(", ")}의 <b>비용, 시설 정보를 한 번에 알아보기</b>
      </>
    );

  // 마퀴 끊김 없이 반복하려면 트랙을 2배로. 카드 수에 비례해 속도 조절.
  const marqueeList = [...list, ...list];
  const duration = Math.max(list.length * 4, 16);

  return (
    <VStack
      width="100%"
      css={{
        padding: "20px 16px",
        borderRadius: "8px",
        border: `1px solid ${colors.gray100}`,
        background: colors.white,
      }}
    >
      <div className="subtitle_16_b gray900">
        {region} 장례식장 {list.length}곳,
        <br />
        비용, 시설, 접근성을 한 번에 알아보세요
      </div>
      <Spacing gap={6} />
      <div className="body_14_m gray600">{desc}</div>
      <Spacing gap={14} />

      <Box
        css={{
          width: "100%",
          overflow: "hidden",
          maskImage: "linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)",
          WebkitMaskImage: "linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)",
        }}
      >
        <Box
          css={{
            display: "flex",
            gap: "10px",
            width: "max-content",
            animation: `${scroll} ${duration}s linear infinite`,
            "&:hover": { animationPlayState: "paused" },
          }}
        >
          {marqueeList.map((p, idx) => (
            <CompareCard key={`${p.id}-${idx}`} p={p} />
          ))}
        </Box>
      </Box>

      <Spacing gap={16} />
      <VStack width="100%" height="46px">
        <LargeButton
          className="button_14_sb"
          buttonType={LARGEL_BUTTON_TYPE.ORANGE}
          onClick={onClickCompare}
        >
          한 번에 비교 견적 받기
        </LargeButton>
      </VStack>

      <CompareRequestModal
        isOpen={open}
        onClose={() => setOpen(false)}
        parlorName={parlorName}
        regionLabel={regionLabel}
      />
    </VStack>
  );
}
